import { Vault, Wallet, CreditCard, Percent, ChartLineUp, TrendUp } from "@phosphor-icons/react";
import KPICard from "./KPICard";
import FinancialScore from "./FinancialScore";
import QuickAdvice from "./QuickAdvice";
import ForecastCard from "./ForecastCard";
import { fmtMoney } from "../../lib/finance-format";

/**
 * Command Center: the six headline KPIs + Quick Advice + Financial Score.
 * Bloomberg-style density with premium typography.
 */
export default function CommandCenter({ metrics, scope }) {
  if (!metrics) return null;
  const nw = metrics.net_worth ?? 0;
  const cash = metrics.cash ?? 0;
  const debt = metrics.total_debt ?? 0;
  const interestYtd = metrics.interest_ytd ?? 0;
  const flow = metrics.monthly_cashflow ?? 0;

  return (
    <div className="space-y-6" data-testid="command-center">
      {/* Row 1: 4 primary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KPICard
          testId="kpi-net-worth"
          label="NET WORTH"
          value={fmtMoney(nw)}
          sublabel={`Activos ${fmtMoney(metrics.assets_total)}`}
          trend={{
            direction: nw >= 0 ? "up" : "down",
            label: nw >= 0 ? "POSITIVO" : "NEGATIVO",
          }}
          accent={nw >= 0 ? "#10B981" : "#EF4444"}
          Icon={Vault}
          big
        />
        <KPICard
          testId="kpi-cash"
          label="CASH"
          value={fmtMoney(cash)}
          sublabel={
            metrics.score_components?.months_covered != null
              ? `${metrics.score_components.months_covered} meses de gastos`
              : "Sin gastos registrados"
          }
          trend={{
            direction:
              (metrics.score_components?.months_covered ?? 0) >= 6
                ? "up"
                : (metrics.score_components?.months_covered ?? 0) >= 3
                ? "flat"
                : "down",
            label:
              (metrics.score_components?.months_covered ?? 0) >= 6
                ? "SEGURO"
                : (metrics.score_components?.months_covered ?? 0) >= 3
                ? "OK"
                : "BAJO",
          }}
          accent="#2563EB"
          Icon={Wallet}
        />
        <KPICard
          testId="kpi-total-debt"
          label="TOTAL DEBT"
          value={fmtMoney(debt)}
          sublabel={
            debt === 0
              ? "Sin deudas"
              : `APR promedio ${metrics.score_components?.weighted_apr?.toFixed(2) || 0}%`
          }
          trend={
            debt > 0
              ? {
                  direction: "down",
                  label: `${metrics.score_components?.weighted_apr?.toFixed(1) || 0}%`,
                }
              : { direction: "up", label: "LIBRE" }
          }
          accent="#EF4444"
          Icon={CreditCard}
        />
        <KPICard
          testId="kpi-interest-ytd"
          label="INTEREST YTD"
          value={fmtMoney(interestYtd)}
          sublabel="Pagado en intereses este año"
          trend={{
            direction: interestYtd > 0 ? "down" : "flat",
            label: interestYtd > 0 ? "COSTOSO" : "$0",
          }}
          accent="#F97316"
          Icon={Percent}
        />
      </div>

      {/* Row 2: Cash Flow (spans 2) + Financial Score (spans 2) */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 md:gap-4">
        <div className="lg:col-span-2">
          <KPICard
            testId="kpi-cashflow"
            label="MONTHLY CASH FLOW"
            value={fmtMoney(flow, { signed: true })}
            sublabel={`Ingresos ${fmtMoney(metrics.monthly_income)} · Gastos ${fmtMoney(
              metrics.monthly_expenses + metrics.monthly_debt_payments
            )}`}
            trend={{
              direction: flow > 0 ? "up" : flow < 0 ? "down" : "flat",
              label:
                metrics.score_components?.savings_rate_pct != null
                  ? `${metrics.score_components.savings_rate_pct}% AHORRO`
                  : flow > 0
                  ? "POSITIVO"
                  : "DÉFICIT",
            }}
            accent={flow >= 0 ? "#10B981" : "#EF4444"}
            Icon={flow >= 0 ? TrendUp : ChartLineUp}
            big
          />
        </div>
        <div className="lg:col-span-2">
          <FinancialScore metrics={metrics} />
        </div>
      </div>

      {/* Row 3: Quick Advice */}
      <QuickAdvice scope={scope} />

      {/* Row 4: AI Forecast (Phase 2 Intelligence) */}
      <ForecastCard scope={scope} />
    </div>
  );
}
